const toNumber = (value) => {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
};

const sumEmissions = (rows) => rows.reduce((total, row) => total + toNumber(row.emissions), 0);
const sumTopLevelEmissions = (rows) => sumEmissions(
  rows.filter(row => Number(row.level || 0) === 0),
);

const catPrefixFor = (id) => {
  const match = String(id || '').toUpperCase().match(/CAT_(\d)(?:_(\d))?/);
  if (!match) return null;
  return match[2] ? `Cat ${match[1]}.${match[2]}` : `Cat ${match[1]}`;
};

const SCOPE3_CATALOG = [
  { id: 'CAT_1', labelKey: 'scopeEmissions.scope3.category.cat1' },
  { id: 'CAT_2', labelKey: 'scopeEmissions.scope3.category.cat2' },
  { id: 'CAT_3', labelKey: 'scopeEmissions.scope3.category.cat3' },
  { id: 'CAT_5', labelKey: 'scopeEmissions.scope3.category.cat5' },
  { id: 'CAT_6', labelKey: 'scopeEmissions.scope3.category.cat6' },
  { id: 'CAT_7', labelKey: 'scopeEmissions.scope3.category.cat7' },
  { id: 'CAT_8', labelKey: 'scopeEmissions.scope3.category.cat8' },
];

/**
 * Builds the report breakdown rows from the four result-summary payloads.
 * Scope 1 by company, Scope 2 by office, Scope 3 by category.
 * The Scope 3 catalog (Cat 1-8) is always shown so an empty year still
 * renders the full structure with zero values. Percentages are relative
 * to the grand total.
 */
export function normalizeReportBreakdown({ scope1, scope2, scope3Af, scope3Ewaste }) {
  const s1Rows = scope1?.rows || [];
  const s2Rows = scope2?.rows || [];
  const s3AfRows = scope3Af?.rows || [];
  const s3EwasteRows = scope3Ewaste?.rows || [];

  const s1Total = sumEmissions(s1Rows);
  const s2Total = sumEmissions(s2Rows);
  // Category rows already include their subcategory totals. E-waste is shown
  // as detail under Cat 5 and must not be added to the report total again.
  const s3Total = sumTopLevelEmissions(s3AfRows);
  const grandTotal = s1Total + s2Total + s3Total;

  const pct = (value) => (grandTotal > 0 ? (value / grandTotal) * 100 : 0);

  const rows = [];

  const pushScope = (scope, labelKey, tone, total) => {
    rows.push({
      id: `scope-${scope}`,
      kind: 'scope',
      scope,
      labelKey,
      level: 0,
      value: total,
      percentage: pct(total),
      tone,
    });
  };

  const pushDetails = (scope, tone, sourceRows, levelOffset = 1) => {
    sourceRows.forEach((row, index) => {
      rows.push({
        id: `${scope}-${row.id || 'row'}-${index}`,
        kind: 'category',
        label: row.label,
        labelKey: row.labelKey,
        level: levelOffset + (row.level || 0),
        value: toNumber(row.emissions),
        percentage: pct(toNumber(row.emissions)),
        tone,
      });
    });
  };

  // Scope 1 by company / legal entity
  pushScope('SCOPE_1', 'scopeEmissions.scope1.title', 'scope1', s1Total);
  pushDetails('SCOPE_1', 'scope1', s1Rows);

  // Scope 2 by office
  pushScope('SCOPE_2', 'scopeEmissions.scope2.title', 'scope2', s2Total);
  pushDetails('SCOPE_2', 'scope2', s2Rows);

  // Scope 3 by category — catalog always rendered (zero when no data)
  pushScope('SCOPE_3', 'scopeEmissions.scope3.title', 'scope3', s3Total);

  const afCategoryValue = {};
  s3AfRows.forEach((row) => {
    if ((row.level || 0) === 0) {
      afCategoryValue[row.id] = (afCategoryValue[row.id] || 0) + toNumber(row.emissions);
    }
  });

  SCOPE3_CATALOG.forEach((cat) => {
    const value = afCategoryValue[cat.id] || 0;
    rows.push({
      id: `SCOPE_3-${cat.id}`,
      kind: 'category',
      labelKey: cat.labelKey,
      catPrefix: catPrefixFor(cat.id),
      level: 1,
      value,
      percentage: pct(value),
      tone: 'scope3',
    });

    if (cat.id === 'CAT_1') {
      s3AfRows.forEach((row, index) => {
        if ((row.level || 0) > 0) {
          rows.push({
            id: `SCOPE_3-${row.id}-${index}`,
            kind: 'category',
            label: row.label,
            labelKey: row.labelKey,
            catPrefix: catPrefixFor(row.id),
            level: 2,
            value: toNumber(row.emissions),
            percentage: pct(toNumber(row.emissions)),
            tone: 'scope3',
          });
        }
      });
    }

    if (cat.id === 'CAT_5') {
      s3EwasteRows.forEach((row, index) => {
        rows.push({
          id: `SCOPE_3-ewaste-${row.id}-${index}`,
          kind: 'category',
          label: row.label,
          labelKey: row.labelKey,
          catPrefix: catPrefixFor(row.id),
          level: 2,
          value: toNumber(row.emissions),
          percentage: pct(toNumber(row.emissions)),
          tone: 'scope3',
        });
      });
    }
  });

  return { rows, total: grandTotal, totalPercentage: 100 };
}

const HISTORICAL_SCOPE_TOTAL_ONLY = 'TOTAL_ONLY';
const HISTORICAL_CATEGORY_STATUS_CALCULATED = 'CALCULATED';

const historicalRowLabel = (item) => {
  if (item?.aggregation_level === 'COMPANY_TOTAL') {
    return item.source_label || item.activity_name || null;
  }
  return item?.facility_name
    || item?.legal_entity_name
    || item?.activity_name
    || item?.source_label
    || item?.category_label
    || item?.category_code
    || null;
};

const normalizeCategoryCode = (code) => {
  const normalized = String(code || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const match = normalized.match(/cat([1-8])/);
  return match ? `CAT_${match[1]}` : null;
};

const aggregateScope3FromDetails = (details) => {
  const byCategory = new Map();
  details.forEach((item) => {
    const code = normalizeCategoryCode(item.category_code) || 'OTHER';
    const current = byCategory.get(code) || {
      category_code: code,
      category_label: item.category_label || item.category_code || code,
      co2_tonnes: 0,
    };
    current.co2_tonnes += toNumber(item.co2_tonnes);
    byCategory.set(code, current);
  });
  return [...byCategory.values()].filter(item => toNumber(item.co2_tonnes) > 0);
};

/**
 * Builds the report breakdown rows from a historical report payload.
 * Scope 1 by entity, Scope 2 by office, Scope 3 by category. Scopes whose
 * coverage is TOTAL_ONLY render only their scope-total row: historical years
 * with totals only must not imply a zero-filled breakdown. Scope 3 categories
 * come from the stored CALCULATED breakdown, falling back to aggregating the
 * detail rows by category code.
 */
export function normalizeHistoricalReportBreakdown(historical) {
  const totals = historical?.scopeTotals || {};
  const coverage = historical?.scopeCoverage || {};
  const details = historical?.details || [];
  const breakdown = historical?.scope3Breakdown || [];

  const grandTotal = toNumber(totals.total);
  const pct = (value) => (grandTotal > 0 ? (value / grandTotal) * 100 : 0);
  const rows = [];

  const pushScope = (scope, labelKey, tone, total) => {
    rows.push({
      id: `scope-${scope}`,
      kind: 'scope',
      scope,
      labelKey,
      level: 0,
      value: total,
      percentage: pct(total),
      tone,
    });
  };

  const hasDetail = (scope) => (
    coverage[scope] && coverage[scope] !== HISTORICAL_SCOPE_TOTAL_ONLY
  );

  const pushDetailRows = (scope, tone) => {
    details
      .filter(item => item.scope === scope)
      .forEach((item, index) => {
        const label = historicalRowLabel(item);
        if (!label) return;
        rows.push({
          id: `${scope}-${item.subcategory_code || item.category_code || item.source_code || 'row'}-${index}`,
          kind: 'category',
          label,
          level: 1,
          value: toNumber(item.co2_tonnes),
          percentage: pct(toNumber(item.co2_tonnes)),
          tone,
        });
      });
  };

  const pushScope3Categories = () => {
    const calculated = breakdown.filter(
      item => item.status === HISTORICAL_CATEGORY_STATUS_CALCULATED
        && toNumber(item.co2_tonnes) > 0,
    );
    const categories = calculated.length
      ? calculated
      : aggregateScope3FromDetails(details);
    categories.forEach((item, index) => {
      const code = normalizeCategoryCode(item.category_code);
      const rawLabel = item.category_label || item.category_code;
      rows.push({
        id: `SCOPE_3-${code || rawLabel || 'other'}-${index}`,
        kind: 'category',
        label: code ? `Cat ${code.slice(-1)}: ${rawLabel}` : rawLabel,
        level: 1,
        value: toNumber(item.co2_tonnes),
        percentage: pct(toNumber(item.co2_tonnes)),
        tone: 'scope3',
      });
    });
  };

  pushScope('SCOPE_1', 'scopeEmissions.scope1.title', 'scope1', toNumber(totals.scope1));
  if (hasDetail('SCOPE_1')) pushDetailRows('SCOPE_1', 'scope1');

  pushScope('SCOPE_2', 'scopeEmissions.scope2.title', 'scope2', toNumber(totals.scope2));
  if (hasDetail('SCOPE_2')) pushDetailRows('SCOPE_2', 'scope2');

  pushScope('SCOPE_3', 'scopeEmissions.scope3.title', 'scope3', toNumber(totals.scope3));
  if (hasDetail('SCOPE_3')) pushScope3Categories();

  return { rows, total: grandTotal, totalPercentage: grandTotal > 0 ? 100 : 0 };
}